import React from 'react'
import { useCurrencySymbol } from '../../../../../utils/currencyUtils';
import CommaConvert from '../../../../../utils/CommaConvert';
import {
  formatAmountComma,
  parseAmountNumber,
} from '../../../../../utils/decimalAmountUtils';

const QuotedExpenseTable = ({ expenseTableData, employeeData, perdiem, averagecost, personOtherExpensesOverride, preserveDecimalHourlyRates = false }) => {
  // Helper function to format numbers with commas

  const USAComma = CommaConvert();
  const currency = useCurrencySymbol();
  const roundAmount2 = (n) =>
    Math.round((parseAmountNumber(n) + Number.EPSILON) * 100) / 100;
  const formatHours = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "";
    const rounded = Math.round((numericValue + Number.EPSILON) * 100) / 100;
    return rounded.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };


  // Function to get employee designation by email
  const getEmployeeDesignation = (email) => {
    const employee = employeeData?.find(emp => emp.EmpEmail === email)
    if (employee) {
      const rawService = String(employee.servicetype || expenseTableData?.TypeService || "").trim();
      const serviceLabel = rawService
        ? rawService.charAt(0).toUpperCase() + rawService.slice(1).toLowerCase()
        : "Service";

      const rawRole =
        employee.customRoles && employee.customRoles.length > 0
          ? String(employee.customRoles[0] || "")
          : String(employee.EmpAuthLevel || "");

      const isManager = rawRole.toLowerCase().includes("manager");
      return `${serviceLabel} ${isManager ? "Manager" : "Employee"}`;
    }
    return 'Unknown'
  }

  // Function to get hourly rate for a person
  // Use the same logic for all managers and operations persons (including Software Manager)
  const getHourlyRate = (email) => {
    const employee = employeeData?.find(emp => emp.EmpEmail === email)
    if (!employee) return 0

    // Check in TaskList array first (most reliable source for hourly rates)
    // This is the same logic used for Software Manager
    const taskEntry = expenseTableData.TaskList?.find(task => {
      return employee && task.taskownerid === employee._id
    })
    
    if (taskEntry) {
        // If hourlysalary exists (already combined), use it directly
        if (taskEntry.hourlysalary !== undefined && taskEntry.hourlysalary !== null) {
          const rate = parseAmountNumber(taskEntry.hourlysalary)
          return preserveDecimalHourlyRates ? rate : Math.round(rate)
        }
      // Otherwise, combine hourlysalaray + overheadpercentage
      const hourlySalary = parseFloat(taskEntry.hourlysalaray) || 0
      const overhead = parseFloat(taskEntry.overheadpercentage) || 0
      return preserveDecimalHourlyRates
        ? parseAmountNumber(hourlySalary) + parseAmountNumber(overhead)
        : Math.round(hourlySalary + overhead)
    }

    // Check in Description array (fallback)
    const descriptionEntry = expenseTableData.Description?.find(desc => {
      return employee && desc.taskownerid === employee._id
    })
    
    if (descriptionEntry) {
      // If hourlysalary exists (already combined), use it directly
      if (descriptionEntry.hourlysalary !== undefined && descriptionEntry.hourlysalary !== null) {
        return parseFloat(descriptionEntry.hourlysalary) || 0
      }
      // Otherwise, combine hourlysalaray + overheadpercentage
      // Use parseFloat for consistent number parsing (same as calculation)
      // Round to nearest integer to ensure exact calculation (e.g., 111.1 becomes 111)
      const hourlySalary = parseFloat(descriptionEntry.hourlysalaray) || 0
      const overhead = parseFloat(descriptionEntry.overheadpercentage) || 0
      return Math.round(hourlySalary + overhead)
    }

    // Check in HardwareList array (fallback)
    const hardwareEntry = expenseTableData.HardwareList?.find(hardware => {
      return employee && hardware.taskownerid === employee._id
    })
    
    if (hardwareEntry) {
      // If hourlysalary exists (already combined), use it directly
      if (hardwareEntry.hourlysalary !== undefined && hardwareEntry.hourlysalary !== null) {
        return parseFloat(hardwareEntry.hourlysalary) || 0
      }
      // Otherwise, combine hourlysalaray + overheadpercentage
      // Use parseFloat for consistent number parsing (same as calculation)
      // Round to nearest integer to ensure exact calculation (e.g., 111.1 becomes 111)
      const hourlySalary = parseFloat(hardwareEntry.hourlysalaray) || 0
      const overhead = parseFloat(hardwareEntry.overheadpercentage) || 0
      return Math.round(hourlySalary + overhead) 
    }

    return 0
  }

  

  
  const getOtherExpenses = (email) => {
    // Get employee ID
    const employee = employeeData?.find(emp => emp.EmpEmail === email)
    if (!employee) return 0

    // Get all descriptions for this employee
    const employeeDescriptions = expenseTableData.Description
      ?.filter(desc => desc.taskownerid === employee._id) || [];

    if (employeeDescriptions.length === 0) return 0;

    // Sum all values for this employee - ensure proper number conversion
    const totalValue = employeeDescriptions.reduce((sum, desc) => {
      return sum + parseAmountNumber(desc.value);
    }, 0);

    // Get the first description for trip duration calculation
    const firstDescription = employeeDescriptions[0];

    // Add trip duration calculation from first description (if it's Installation service)
    let tripExpenses = 0;
    if (firstDescription.typeservice &&
      firstDescription.typeservice.toLowerCase().includes('installation') &&
      firstDescription.tripduration) {
      const tripDuration = Number(firstDescription.tripduration) || 0
      const perdiemNum = Number(perdiem) || 0
      const averagecostNum = Number(averagecost) || 0
      tripExpenses = (tripDuration * perdiemNum) + (tripDuration * averagecostNum);
    }

    return totalValue + tripExpenses;
  }

  // Function to get total hours for non-hardware TypeService
  const getTotalHours = (email) => {
    // Get employee ID
    const employee = employeeData?.find(emp => emp.EmpEmail === email)
    if (!employee) return 0

    // For non-Hardware TypeServices, use hoursassigned from TaskList
    // Ensure proper number conversion for accurate calculations
    const taskHours = expenseTableData.TaskList
      ?.filter(task => task.taskownerid === employee._id)
      ?.reduce((sum, task) => {
              const hours = parseFloat(task.hoursassigned) || 0
        return sum + hours
      }, 0) || 0

    return taskHours
  }

  const getMaxCommonHardwareTotal = () => {
    if (!expenseTableData?.HardwareList?.length) return 0;

    // Group by partno (you can switch to assemblepartdescription if needed)
    const grouped = expenseTableData.HardwareList.reduce((acc, item) => {
      const key = item.assemblepartdescription; // or item.assemblepartdescription
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    // Take max totalprice per group
    let total = 0;
    Object.values(grouped).forEach(items => {
      const maxItem = items.reduce(
        (max, curr) => (curr.totalprice > max.totalprice ? curr : max),
        items[0]
      );
      total += maxItem.totalprice || 0;
    });

    return total;
  };


  const isManager = (email) => {
    const designation = getEmployeeDesignation(email);
    return designation?.toLowerCase().includes("manager");
  };

  // Calculate totals for each assigned person
  // When personOtherExpensesOverride is provided (e.g. from Project Assign), use it so summary matches the project list (processed manager/OP data)
  // Sort so Manager comes first, then operation persons in original order - matches Wizard "Manager" then "Cloud Employee 1", "Cloud Employee 2" display
  const calculatePersonTotals = () => {
    if (!expenseTableData?.Assignedperson || !employeeData) return []
    const maxHardwareTotal = getMaxCommonHardwareTotal();

    const sortedPersons = [...expenseTableData.Assignedperson].sort((a, b) => {
      const aManager = isManager(a.email)
      const bManager = isManager(b.email)
      if (aManager && !bManager) return -1
      if (!aManager && bManager) return 1
      return 0
    })

    return sortedPersons.map(person => {
      const hourlyRate = getHourlyRate(person.email)
      const overrideEntry = personOtherExpensesOverride?.find((o) => o.email === person.email)
      const otherExpenses = overrideEntry != null
        ? (Number(overrideEntry.otherExpenses) || 0)
        : getOtherExpenses(person.email)
      console.log("Hdfhd hourlyRate", hourlyRate)

      if (expenseTableData.TypeService === "Hardware") {
        const manager = isManager(person.email);

        // Only manager gets hardware cost
        const totalHardwarePrice = manager ? Math.round(maxHardwareTotal || 0) : 0;

        const totalEfforts = totalHardwarePrice;
        const otherExpensesRounded = roundAmount2(otherExpenses)
        const totalExpenses = roundAmount2(totalEfforts + otherExpensesRounded);

        return {
          name: person.name,
          email: person.email,
          designation: getEmployeeDesignation(person.email),
          hourlyRate,
          totalHardwarePrice,
          otherExpenses: otherExpensesRounded,
          totalEfforts,
          totalExpenses,
          isHardware: true
        };
      } else {
        // For other TypeServices (both operations manager and operations person)
        // Use the same logic as Software Manager: hourlyRate × totalHours + otherExpenses = totalExpenses
        
        // Get total hours for this person
        const totalHours = parseFloat(getTotalHours(person.email)) || 0
        
        // Get hourly rate for this person (already calculated at line 173)
        // Ensure we use the same hourly rate retrieval logic for all
        const hourlyRateNum = parseFloat(hourlyRate) || 0
        
        const otherExpensesNum = roundAmount2(otherExpenses)
        const totalExpenses = roundAmount2(
          hourlyRateNum * totalHours + otherExpensesNum
        )
        
        // Calculate total efforts for display: hourlyRate × totalHours
        // Round to nearest integer for exact calculation
        const totalEfforts = Math.round(hourlyRateNum * totalHours)
        
        return {
          name: person.name,
          email: person.email,
          designation: getEmployeeDesignation(person.email),
          hourlyRate: hourlyRateNum,
          totalHours,
          otherExpenses: otherExpensesNum,
          totalEfforts,
          totalExpenses,
          isHardware: false
        }
      }
    })
  }

  const calculateGrandTotals = () => {
    const personTotals = calculatePersonTotals()
    // Calculate sums using integer arithmetic to avoid floating point precision issues
    // Each person's values are already rounded to integers, so sum should be exact
    let totalEffortsSum = 0
    let otherExpensesSum = 0
    let totalExpensesSum = 0
    
    personTotals.forEach(person => {
      // Ensure we're adding integers (already rounded in calculatePersonTotals)
      totalEffortsSum += Math.round(person.totalEfforts || 0)
      otherExpensesSum += roundAmount2(person.otherExpenses || 0)
      totalExpensesSum += roundAmount2(person.totalExpenses || 0)
    })
    
    return {
      totalEfforts: totalEffortsSum,
      otherExpenses: otherExpensesSum,
      totalExpenses: totalExpensesSum // Should match detailed table total exactly
    }
  }

  const personTotals = calculatePersonTotals()

  console.log("fhdfjdj personTotals", personTotals)
  const grandTotals = calculateGrandTotals()
  console.log("fhdfjdj grandTotals", grandTotals)

  return (
    <div className='flex justify-end p-3'>
      <div
        style={{ scrollBehavior: 'smooth' }}
        className="maintable w-[55%] overflow-auto border">

        {/* Table Header */}
        <div className="w-full flex text-xs ">
          <div className="w-[25%] flex justify-center align-middle items-center p-1  text-center bg-[#e8e8e8]">
            Resources Designation
          </div>
          {/* <div className="w-[20%] flex justify-center align-middle items-center p-4 border text-center bg-blue-500 text-white">
            Rate Per Hour ($)
          </div> */}
          <div className="w-[25%] flex justify-center align-middle items-center p-1  text-center bg-[#e8e8e8]">
            {expenseTableData?.TypeService === 'Hardware' ? `Hardware Total (${currency})` : 'Efforts In Hours'}
          </div>
          {/* <div className="w-[16.6%] flex justify-center align-middle items-center p-4 border text-center bg-blue-500 text-white">
            {expenseTableData.TypeService === 'Hardware' ? '-' : 'Total Efforts ($)'}
          </div> */}
          <div className="w-[25%] flex flex-col justify-center align-middle items-center p-1  text-center bg-[#e8e8e8]">
            Other Expenses ({currency})
          </div>
          <div className="w-[25%] flex justify-center align-middle items-center p-1  text-center bg-[#e8e8e8]">
            Total Expenses ({currency})
          </div>
        </div>

        {/* Table Footer - Grand Totals */}
        <div className="Totaltable flex w-full text-center text-xs">
          <div className="w-[25%] border-b p-1 text-center ">
            Total
          </div>
          {/* <div className="w-[20%] border p-4 text-center bg-gray-100">
          </div> */}

          <div className="w-[25%] border-b p-1 text-center">
            {/* {USAComma(grandTotals.totalEfforts)} */}
          </div>
          <div className="w-[25%] border-b p-1 text-center">
            {formatAmountComma(grandTotals.otherExpenses) || ""}
          </div>
          <div className="w-[25%] border-b p-1 text-center">
            {formatAmountComma(grandTotals.totalExpenses) || ""}
          </div>
        </div>

        {/* Table Body */}
        <div className="Employee_Efforts_Calculation w-full">
          {personTotals.map((person, index) => (
            <div key={index} className="flex text-xs w-full">
              <div className="w-full">
                <div className="flex w-full">
                  <div className="w-[25%] p-1 capitalize flex justify-center items-center text-center border-b">
                    {person.designation}
                  </div>
                  {/* <div className="w-[20%] flex justify-center items-center text-center border">
                    {person.hourlyRate}
                  </div> */}
                  <div className="w-[25%] p-1 flex justify-center items-center text-center border-b">
                    {person.isHardware ? USAComma(person.totalHardwarePrice) : formatHours(person.totalHours)}
                  </div>
                  {/* <div className="w-[16.6%] p-4 flex justify-center items-center text-center border">
                    {person.isHardware ? '-' : (person.totalEfforts)}
                  </div> */}
                  <div className="w-[25%] p-1 flex justify-center items-center text-center border-b">
                    {formatAmountComma(person.otherExpenses) || " "}
                  </div>
                  <div className="w-[25%] p-1 flex justify-center items-center text-center border-b">
                    {formatAmountComma(person.totalExpenses) || " "}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>


      </div>
    </div>
  )
}

export default QuotedExpenseTable